import { useState } from 'react';

import { ApiError, api, type Me, type PlanSummary } from '../lib/api';
import { Alert, Badge, Card, PageHead, Tile, useLoader } from '../components/ui';

/**
 * Section 4.10, backed by Phase 7.
 *
 * Everything here is real except the prices, and the page says so where they
 * appear. Section 9.4 is explicit that the tier table is a starting structure
 * for a business decision, not a pricing recommendation, so `price_is_placeholder`
 * comes back on every plan and this screen surfaces it rather than presenting
 * the numbers as settled.
 */

function money(cents: number | null): string {
  if (cents === null) return 'Custom';
  if (cents === 0) return '$0';
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function limit(value: number | null): string {
  return value === null ? 'Unlimited' : value.toLocaleString();
}

/** A day-by-day column chart. Inline SVG rather than a chart library: this is
 * the only chart on the screen and the dependency would cost more than it. */
function UsageChart({ days }: { days: { date: string; environment: string; sent: number }[] }) {
  const byDate = new Map<string, { live: number; test: number }>();
  for (const row of days) {
    const entry = byDate.get(row.date) ?? { live: 0, test: 0 };
    if (row.environment === 'live') entry.live += row.sent;
    else entry.test += row.sent;
    byDate.set(row.date, entry);
  }

  const points = [...byDate.entries()].sort(([a], [b]) => a.localeCompare(b));
  const peak = Math.max(1, ...points.map(([, v]) => v.live + v.test));
  const width = Math.max(points.length * 12, 120);

  if (!points.length) {
    return <div className="empty">No sends in this window.</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg
        viewBox={`0 0 ${width} 80`}
        style={{ width: '100%', minWidth: width, height: 110 }}
        role="img"
        aria-label="Daily sends"
      >
        {points.map(([day, value], index) => {
          const liveHeight = (value.live / peak) * 70;
          const testHeight = (value.test / peak) * 70;
          return (
            <g key={day} transform={`translate(${index * 12}, 0)`}>
              <title>{`${day}: ${value.live} live, ${value.test} test`}</title>
              <rect
                x={2}
                y={75 - liveHeight}
                width={8}
                height={liveHeight}
                fill="var(--accent)"
                rx={1}
              />
              {/* Test usage stacks on top in a muted tone: visible, and clearly
                  not part of the number that gets billed. */}
              <rect
                x={2}
                y={75 - liveHeight - testHeight}
                width={8}
                height={testHeight}
                fill="var(--border)"
                rx={1}
              />
            </g>
          );
        })}
        <line x1={0} y1={76} x2={width} y2={76} stroke="var(--border)" strokeWidth={1} />
      </svg>
    </div>
  );
}

export function Billing({ me }: { me: Me }) {
  const billing = useLoader(() => api.billing.get());
  const catalogue = useLoader(() => api.billing.plans());
  const usage = useLoader(() => api.billing.usage(30));
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const data = billing.data;
  const plans: PlanSummary[] = catalogue.data?.plans ?? [];
  const used = data?.usage.used ?? 0;
  const included = data?.usage.included ?? 0;
  const percent = included ? Math.min(100, Math.round((used / included) * 100)) : 0;

  async function go(action: 'checkout' | 'portal', plan?: string) {
    setBusy(plan ?? 'portal');
    setError(null);
    try {
      const { url } =
        action === 'checkout'
          ? await api.billing.checkout(plan as string)
          : await api.billing.portal();
      // A full navigation, not a new tab: Stripe returns the customer here
      // afterwards, and a popup blocker must not be able to swallow a payment.
      window.location.href = url;
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not reach the billing provider',
      );
      setBusy(null);
    }
  }

  return (
    <>
      <PageHead
        title="Billing"
        subtitle="Usage this period, the plan it counts against, and what it would cost."
        actions={
          data?.subscription.has_payment_method ? (
            <button
              type="button"
              className="btn"
              disabled={busy !== null}
              onClick={() => go('portal')}
            >
              {busy === 'portal' ? 'Opening…' : 'Invoices & payment method'}
            </button>
          ) : undefined
        }
      />

      {error && <Alert kind="error">{error}</Alert>}
      {billing.error && <Alert kind="error">{billing.error}</Alert>}

      {data?.usage.blocked && (
        <Alert kind="error">
          <strong>Sending is stopped.</strong> {data.usage.reason}
        </Alert>
      )}
      {data?.subscription.status === 'past_due' && (
        <Alert kind="warn">
          The last payment failed. Sending continues while Stripe retries — update the
          card from <strong>Invoices &amp; payment method</strong> before the
          subscription is cancelled.
        </Alert>
      )}
      {data?.subscription.cancel_at_period_end && (
        <Alert kind="warn">
          This subscription ends on{' '}
          {data.subscription.current_period_end?.slice(0, 10) ?? 'the period end'} and the
          account returns to Free. Nothing is deleted.
        </Alert>
      )}

      <div className="tiles">
        <Tile
          label="Current plan"
          value={data?.plan.label ?? me.tenant.plan}
          hint={
            data?.subscription.has_payment_method ? 'card on file' : 'no card on file'
          }
        />
        <Tile
          label="Billable sends"
          value={used.toLocaleString()}
          hint="live only — test mode is free"
        />
        <Tile
          label="Included volume"
          value={included.toLocaleString()}
          hint={`${(data?.usage.remaining ?? 0).toLocaleString()} remaining`}
        />
        <Tile
          label="Overage so far"
          value={money(data?.overage.estimated_overage_cents ?? 0)}
          hint="estimate — Stripe computes the invoice"
        />
      </div>

      <Card title="Usage against plan">
        <div className="bar-row">
          <span>Monthly volume</span>
          <span className="bar-track">
            <span
              className="bar-fill"
              style={{
                width: `${percent}%`,
                background: used > included ? 'var(--danger)' : 'var(--accent)',
              }}
            />
          </span>
          <span className="mono" style={{ textAlign: 'right' }}>
            {percent}%
          </span>
        </div>
        {(data?.usage.over_by ?? 0) > 0 && (
          <p className="hint" style={{ marginTop: 8 }}>
            {(data?.usage.over_by ?? 0).toLocaleString()} over the included volume.
            {data?.plan.hard_volume_cap
              ? ' This plan has no card on file, so there is nothing to bill the overage to and sending stops.'
              : ` Billed at ${money(data?.plan.overage_per_thousand_cents ?? 0)} per 1,000.`}
          </p>
        )}
        <p className="hint" style={{ marginTop: 8 }}>
          Only live sends count. A test key never reaches SES, so it never reaches an
          invoice either.
        </p>
      </Card>

      <Card title="Last 30 days">
        <UsageChart days={usage.data?.days ?? []} />
        <div className="row" style={{ gap: 16, marginTop: 10 }}>
          <span className="hint">
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                background: 'var(--accent)',
                marginRight: 6,
              }}
            />
            {(usage.data?.billable_total ?? 0).toLocaleString()} live (billable)
          </span>
          <span className="hint">
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                background: 'var(--border)',
                marginRight: 6,
              }}
            />
            {(usage.data?.test_total ?? 0).toLocaleString()} test (free)
          </span>
        </div>
      </Card>

      <Card tight title="Limits">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Limit</th>
                <th style={{ textAlign: 'right' }}>Used</th>
                <th style={{ textAlign: 'right' }}>Included</th>
              </tr>
            </thead>
            <tbody>
              {(data?.limits ?? []).map((row) => (
                <tr key={row.name}>
                  <td>{row.name.replace(/_/g, ' ')}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {row.used.toLocaleString()}
                  </td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {limit(row.limit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="hint" style={{ padding: '10px 16px' }}>
          The platform subdomain does not count against custom domains — it carries no
          reputation risk and costs nothing to offer.
        </div>
      </Card>

      <Card tight title="Plans">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Tier</th>
                <th>Price</th>
                <th>Included</th>
                <th>Overage /1k</th>
                <th>Domains</th>
                <th>Seats</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.key}>
                  <td>
                    <strong>{plan.label}</strong>{' '}
                    {plan.key === (data?.plan.key ?? me.tenant.plan) && (
                      <Badge kind="ok">current</Badge>
                    )}
                  </td>
                  <td>{money(plan.price_cents)}</td>
                  <td className="mono">{plan.included_volume.toLocaleString()}</td>
                  <td className="mono">
                    {plan.overage_per_thousand_cents === null
                      ? '—'
                      : money(plan.overage_per_thousand_cents)}
                  </td>
                  <td>{limit(plan.custom_domains)}</td>
                  <td>{limit(plan.team_seats)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {plan.key === (data?.plan.key ?? me.tenant.plan) ? null : plan.self_serve ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={busy !== null || !plan.purchasable}
                        title={
                          plan.purchasable
                            ? undefined
                            : 'No Stripe price is configured for this plan yet'
                        }
                        onClick={() => go('checkout', plan.key)}
                      >
                        {busy === plan.key ? 'Opening…' : 'Choose'}
                      </button>
                    ) : (
                      <span className="hint">Talk to sales</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Alert kind="info">
        <strong>These prices are placeholders.</strong> Section 9.4 of the spec is
        explicit that the tier table is a starting structure for a pricing decision, not a
        recommendation. The limits are real and enforced; the numbers in the price column
        are meant to be replaced from your own Stripe product catalogue. Everything else
        on this page — usage, overage, subscription state — is live.
      </Alert>
    </>
  );
}
